import React, { useState, useEffect, useRef } from 'react';
import './GuitarTabs.css';
import { Button } from "@mui/material"
import NavBar from '../Widgets/NavBar';
import Footer from '../Widgets/Footer';

const strings = ['e', 'B', 'G', 'D', 'A', 'E'];
const NOTES_PER_ROW = 38;

export default function GuitarTabs() {
    const [tab, setTab] = useState(() =>
        Array.from({ length: 6 }, () => Array(NOTES_PER_ROW).fill(''))
    );
    const [cursor, setCursor] = useState({ row: 0, col: 0 });
    const inputRefs = useRef({});

    // Dynamically split the tab into chunks (blocks of 16 cols)
    const chunkTab = () => {
        const chunks = [];
        const totalCols = tab[0].length;
        for (let i = 0; i < totalCols; i += NOTES_PER_ROW) {
            chunks.push(tab.map(row => row.slice(i, i + NOTES_PER_ROW)));
        }
        return chunks;
    };

    useEffect(() => {
        const keyListener = (e) => {
            let { row, col } = cursor;
            let updateNeeded = false;

            if (e.key === 'ArrowRight') {
                col++;
                // Check if we need to add a new column
                if (col >= tab[0].length) {
                    const extendedTab = tab.map(line => [...line, '']);
                    setTab(extendedTab);
                }
            } else if (e.key === 'ArrowLeft') {
                col = Math.max(0, col - 1);
            } else if (e.key === 'ArrowUp') {
                row = Math.max(0, row - 1);
            } else if (e.key === 'ArrowDown') {
                row = Math.min(5, row + 1);
            } else {
                return;
            }

            e.preventDefault();
            setCursor({ row, col });
            setTimeout(() => focusInput(row, col), 0); // Delay focus after state update
        };

        window.addEventListener('keydown', keyListener);
        return () => window.removeEventListener('keydown', keyListener);
    }, [cursor, tab]);

    const handleChange = (row, col, value) => {
        const newTab = tab.map((line, i) =>
            i === row ? line.map((v, j) => (j === col ? value : v)) : line
        );
        setTab(newTab);
    };

    const focusInput = (row, col) => {
        const key = `${row}-${col}`;
        const input = inputRefs.current[key];
        if (input) input.focus();
    };

    const chunks = chunkTab();

    function importTab() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.txt';

        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const text = await file.text();
            const lines = text.trim().split('\n');

            if (lines.length !== 6) {
                alert("Invalid tab format. Expecting 6 lines.");
                return;
            }

            const newTab = Array(6).fill().map(() => []);

            for (let i = 0; i < 6; i++) {
                const line = lines[i];
                if (!line.startsWith(strings[i] + '|')) {
                    alert(`Line ${i + 1} must start with "${strings[i]}|"`);
                    return;
                }

                const content = line.slice(2); // remove "E|"
                for (let j = 0; j < content.length; j++) {
                    const char = content[j];
                    // Attempt to group double digits
                    const prev = content[j - 1] || '';
                    const next = content[j + 1] || '';

                    if (char.match(/\d/) && next.match(/\d/)) {
                        newTab[i].push(char + next);
                        j++; // skip next char
                    } else if (char.match(/[-\d]/)) {
                        newTab[i].push(char === '-' ? '' : char);
                    }
                }
            }

            setTab(newTab);
            setCursor({ row: 0, col: 0 });
            setTimeout(() => focusInput(0, 0), 0);
        };

        input.click();
    }


    function exportTab() {
        const lines = tab.map((line, i) => `${strings[i]}|${line.map(cell => cell || '-').join('')}`);
        const content = lines.join('\n');

        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'guitar_tab.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    function reset() {
        setTab(Array.from({ length: 6 }, () => Array(NOTES_PER_ROW).fill('')))
    }

    return (
        <div className='page-wrapper'>
            <header className="py-5">
                <div className="container">
                    <div className="row">
                        <NavBar selected="guitar-tabs-page"></NavBar>
                    </div>
                </div>
            </header>

            <div className='container py-5'>
                <div className='d-flex justify-content-between align-items-center'>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={reset}>
                        Reset
                    </Button>
                    <div>
                        <span>You can use the arrow keys to move up and down.</span><br></br>
                        <span>To generate new columns simply go to the most right edge and click on the right arrow key.</span>
                    </div>
                    <div>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={importTab}>
                            Import
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            className='mx-2'
                            onClick={exportTab}>
                            Export
                        </Button>
                    </div>
                </div>
                <div className="tab-wrapper">
                    {chunks.map((chunk, chunkIndex) => (
                        <div className="tab-block mb-4" key={chunkIndex}>
                            {strings.map((s, rowIndex) => (
                                <div className="tab-row" key={s}>
                                    <div className="string-label">{s}|</div>
                                    {chunk[rowIndex].map((note, colOffset) => {
                                        const realCol = chunkIndex * NOTES_PER_ROW + colOffset;
                                        const key = `${rowIndex}-${realCol}`;
                                        return (
                                            <input
                                                key={key}
                                                className={`tab-cell ${cursor.row === rowIndex && cursor.col === realCol ? 'active' : ''}`}
                                                ref={(el) => (inputRefs.current[key] = el)}
                                                value={note}
                                                onChange={(e) => handleChange(rowIndex, realCol, e.target.value)}
                                                onClick={() => setCursor({ row: rowIndex, col: realCol })}
                                                maxLength={2}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            <Footer></Footer>
        </div>
    );
}
